import type { FlowRow } from '../schemas/entities';
import type { Format, Sheet } from '../schemas/format';

export type SheetLayout = 'single' | 'perCase';

export const ALL_SHEET_ID = 'all';

/** One sheet with every speech in speaking order (C1, C2, CF1, R1…): both cases' arguments share the rows. */
export const singleSheet = (f: Format): Sheet => ({ id: ALL_SHEET_ID, title: 'Flow', owner: null, segmentIds: f.segments.map((s) => s.id) });

/**
 * Sheets to show for a round. A round that was flowed on one sheet always shows one sheet (its rows belong to
 * neither case), whatever the setting says.
 */
export const sheetsFor = (f: Format, layout: SheetLayout, rows: Pick<FlowRow, 'sheetId'>[]): Sheet[] =>
  layout === 'single' || rows.some((r) => r.sheetId === ALL_SHEET_ID) ? [singleSheet(f)] : f.sheets;

/**
 * Rows on a sheet, in order. The single sheet shows every row of the round, so rounds flowed per case still
 * show everything: grouped by the sheet they were written on, then by position.
 */
export const rowsForSheet = <R extends Pick<FlowRow, 'sheetId' | 'rank'>>(f: Format, sheet: Sheet, rows: R[]): R[] => {
  if (sheet.id !== ALL_SHEET_ID) return rows.filter((r) => r.sheetId === sheet.id).sort((a, b) => a.rank - b.rank);
  // One-sheet rows go last, so rows added "at the end" of an older per-case round really are at the end.
  const order = new Map([...f.sheets.map((s) => s.id), ALL_SHEET_ID].map((id, i) => [id, i]));
  return [...rows].sort((a, b) => (order.get(a.sheetId) ?? 99) - (order.get(b.sheetId) ?? 99) || a.rank - b.rank);
};
